import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import {
  Box,
  ClickAwayListener,
  Divider,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import useColors from "../../api/hooks/useColors";
import SvgToIcon from "../../icons/SvgToIcon";
import { icons } from "../../scenes/stack/environment/icons/iconExport";

const AssigneeMenu = ({
  onAssigneeChange,
  initialAssignee = null,
  anchorEl,
  onClose,
  platformUsers = [],
}) => {
  const colors = useColors();
  const [selectedAssignee, setSelectedAssignee] = useState(initialAssignee);
  const menuRef = useRef(null);

  useEffect(() => {
    if (initialAssignee) {
      setSelectedAssignee(initialAssignee);
    }
  }, [initialAssignee]);

  const handleAssigneeSelect = (assignee) => {
    setSelectedAssignee(assignee);
    onAssigneeChange(assignee);
    onClose();
  };

  if (!anchorEl) return null;

  return (
    <Popper
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: 1300, marginTop: 2 }}
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [0, 2],
          },
        },
      ]}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          ref={menuRef}
          elevation={2}
          sx={{
            width: 200,
            maxHeight: 400,
            overflowY: "auto",
            borderRadius: "8px",
            p: 1,
          }}
        >
          <Box
            sx={{
              height: "36px",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.text_secondary,
                pl: 1,
              }}
            >
              Assign to...
            </Typography>
          </Box>
          <Divider />

          {/* No Assignee Option */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1,
              cursor: "pointer",
              backgroundColor:
                selectedAssignee === null
                  ? colors.grey_8percent
                  : "transparent",
              "&:hover": {
                backgroundColor: colors.grey_4percent,
              },
            }}
            onClick={() => handleAssigneeSelect(null)}
          >
            <SvgToIcon
              IconLogo={icons.AssigneeIcon}
              style={{
                width: "16px",
                height: "16px",
              }}
            />
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Typography variant="caption">No Assignee</Typography>
              {selectedAssignee === null && (
                <DoneRoundedIcon
                  sx={{
                    fontSize: "16px",
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Members Section */}
          {platformUsers && platformUsers.length > 0 && (
            <>
              <Typography
                variant="overline"
                sx={{
                  mt: 2,
                  mb: 1,
                  color: colors.text_secondary,
                }}
              >
                Members
              </Typography>

              {platformUsers.map((user) => (
                <Box
                  key={user.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1,
                    borderRadius: "4px",
                    cursor: "pointer",
                    backgroundColor:
                      selectedAssignee?.email === user.email
                        ? colors.grey_8percent
                        : "transparent",
                    "&:hover": {
                      backgroundColor: colors.grey_4percent,
                    },
                  }}
                  onClick={() => handleAssigneeSelect(user)}
                >
                  <Box
                    sx={{
                      minWidth: "16px",
                      minHeight: "16px",
                      borderRadius: "50%",
                      backgroundColor: colors.primary_main,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "10px",
                      color: colors.common_white,
                    }}
                  >
                    {user.display_name
                      ? user.display_name.charAt(0).toUpperCase()
                      : user.email.charAt(0).toUpperCase()}
                  </Box>

                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Typography variant="caption">
                      {user.display_name || user.email || "Member"}
                    </Typography>
                    {selectedAssignee?.email === user.email && (
                      <DoneRoundedIcon
                        sx={{
                          fontSize: "16px",
                        }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default AssigneeMenu;
